import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Html5Qrcode } from "html5-qrcode";

const QR_READER_ID = "sensor-qr-reader";

const QrScannerPanel = ({ onScan }) => {
  const { t } = useTranslation();
  const scannerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [isScanning, setIsScanning] = useState(false);
  const [isFileScanning, setIsFileScanning] = useState(false);
  const [isBusy, setIsBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const disposeScanner = async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;

    if (!scanner) return;

    // html5-qrcode can return undefined while a camera start is still pending.
    // Promise.resolve keeps the cleanup safe in both cases.
    await Promise.resolve().then(() => scanner.stop()).catch(() => undefined);
    await Promise.resolve().then(() => scanner.clear()).catch(() => undefined);
  };

  const handleDecodedValue = async (decodedText) => {
    const sensorCode = decodedText.trim();
    if (!sensorCode) return;

    onScan(sensorCode);
    setMessage(
      t("qr_sensor_code_detected", {
        defaultValue: "Code du capteur détecté et ajouté au formulaire.",
      })
    );
    setError("");
    setIsScanning(false);
    setIsFileScanning(false);
    await disposeScanner();
  };

  const startScanner = async () => {
    setError("");
    setMessage("");
    setIsBusy(true);
    setIsScanning(true);

    try {
      await disposeScanner();
      const scanner = new Html5Qrcode(QR_READER_ID, false);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        {
          fps: 10,
          qrbox: { width: 240, height: 240 },
          aspectRatio: 1,
          disableFlip: true,
        },
        handleDecodedValue,
        () => undefined
      );
      setIsScanning(true);
    } catch (scanError) {
      await disposeScanner();
      setIsScanning(false);
      setError(
        t("qr_camera_error", {
          defaultValue:
            "Impossible d’ouvrir la caméra. Autorisez son accès ou importez une image du QR code.",
        })
      );
    } finally {
      setIsBusy(false);
    }
  };

  const stopScanner = async () => {
    setIsBusy(true);
    await disposeScanner();
    setIsScanning(false);
    setIsBusy(false);
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setError("");
    setMessage("");
    setIsBusy(true);
    setIsFileScanning(true);

    try {
      await disposeScanner();
      const scanner = new Html5Qrcode(QR_READER_ID, false);
      scannerRef.current = scanner;
      const decodedText = await scanner.scanFile(file, true);
      await handleDecodedValue(decodedText);
    } catch (_scanError) {
      setError(
        t("qr_image_no_code_found", {
          defaultValue: "Aucun QR code lisible n’a été trouvé dans cette image.",
        })
      );
      await disposeScanner();
    } finally {
      setIsFileScanning(false);
      setIsBusy(false);
    }
  };

  useEffect(() => () => {
    disposeScanner();
  }, []);

  return (
    <div className="sensor-qr-panel">
      <div className="sensor-qr-heading">
        <span><i className="fas fa-qrcode" aria-hidden="true" /></span>
        <div>
          <small>{t("qr_quick_entry", { defaultValue: "Saisie rapide" })}</small>
          <h5>{t("qr_scan_sensor_code", { defaultValue: "Scanner le code QR du capteur" })}</h5>
        </div>
      </div>

      <p className="sensor-qr-help">
        {t("qr_help_text", {
          defaultValue:
            "Ouvrez la caméra uniquement lorsque vous êtes prêt, ou importez une image depuis votre ordinateur.",
        })}
      </p>

      <div className="sensor-qr-actions">
        {!isScanning ? (
          <button type="button" className="sensor-qr-start" onClick={startScanner} disabled={isBusy}>
            <i className="fas fa-camera" aria-hidden="true" />
            {isBusy
              ? t("qr_opening", { defaultValue: "Ouverture..." })
              : t("qr_start_scan", { defaultValue: "Démarrer le scan" })}
          </button>
        ) : (
          <button type="button" className="sensor-qr-stop" onClick={stopScanner} disabled={isBusy}>
            <i className="fas fa-stop" aria-hidden="true" />
            {t("qr_stop_camera", { defaultValue: "Arrêter la caméra" })}
          </button>
        )}
        <button
          type="button"
          className="sensor-qr-upload"
          onClick={() => fileInputRef.current?.click()}
          disabled={isBusy}
        >
          <i className="fas fa-image" aria-hidden="true" />
          {t("qr_choose_image", { defaultValue: "Choisir une image" })}
        </button>
        <input
          ref={fileInputRef}
          className="visually-hidden"
          type="file"
          accept="image/*"
          onChange={handleImageUpload}
        />
      </div>

      <div className={`sensor-qr-camera ${isScanning || isFileScanning ? "is-visible" : ""}`} aria-live="polite">
        <div id={QR_READER_ID} />
      </div>
      {message && <p className="sensor-qr-status is-success">{message}</p>}
      {error && <p className="sensor-qr-status is-error">{error}</p>}
    </div>
  );
};

export default QrScannerPanel;
